import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyEntitlements } from "@/lib/entitlements.functions";
import { useAuth } from "@/lib/auth";

export type Entitlements = {
  tier: "free" | "tutor" | "center" | "school" | "enterprise";
  dashboard: boolean;
  reports: boolean;
  finance: boolean;
  bulkNotify: boolean;
  exportExcel: boolean;
  exportPdf: boolean;
  scheduledNotify: boolean;
  documentsEnabled: boolean;
  teacherNotify: boolean;
  maxClasses: number;
  maxStudents: number;
  maxTeachers: number;
  currentClasses: number;
  currentStudents: number;
  currentTeachers: number;
  isLoading: boolean;
};

const DEMO_ENT: Entitlements = {
  tier: "school",
  dashboard: true, reports: true, finance: true,
  bulkNotify: true, exportExcel: true, exportPdf: true, scheduledNotify: true,
  documentsEnabled: true, teacherNotify: true,
  maxClasses: -1, maxStudents: -1, maxTeachers: -1,
  currentClasses: 0, currentStudents: 0, currentTeachers: 0,
  isLoading: false,
};

export function useEntitlements(): Entitlements {
  const { user, isDemo } = useAuth();
  const getEntitlementsFn = useServerFn(getMyEntitlements);

  const q = useQuery({
    queryKey: ["entitlements", user?.id],
    enabled: !!user && !isDemo,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    queryFn: () => getEntitlementsFn({}),
  });

  if (isDemo) return DEMO_ENT;

  return {
    tier: (q.data?.tier as Entitlements["tier"]) ?? "free",
    dashboard: q.data?.dashboard ?? false,
    reports: q.data?.reports ?? false,
    finance: q.data?.finance ?? false,
    bulkNotify: q.data?.bulkNotify ?? false,
    exportExcel: q.data?.exportExcel ?? false,
    exportPdf: q.data?.exportPdf ?? false,
    scheduledNotify: q.data?.scheduledNotify ?? false,
    documentsEnabled: q.data?.documentsEnabled ?? false,
    teacherNotify: q.data?.teacherNotify ?? false,
    maxClasses: q.data?.maxClasses ?? 1,
    maxStudents: q.data?.maxStudents ?? 5,
    maxTeachers: q.data?.maxTeachers ?? 0,
    currentClasses: q.data?.currentClasses ?? 0,
    currentStudents: q.data?.currentStudents ?? 0,
    currentTeachers: q.data?.currentTeachers ?? 0,
    isLoading: q.isLoading,
  };
}
